import styled from '@emotion/styled'
import { MultipleEditPayload, PrebidModule, PrebidModuleDataBySite, PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { Site } from '@pubstack/common/src/site'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { Flyout } from '~/components/Flyout'
import { FoldingContent } from '~/components/FoldingContent'
import { BaseInputElement, Input } from '~/components/Input'
import { JSONEditorInput } from '~/components/JSONEditor'
import { BulletIcon, RadioItemWrapper } from '~/components/RadioButtonGroup'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Status } from '~/components/Status'
import { useToast } from '~/components/Toast/useToasts'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSearch, handleTableSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { JSON_SPACE_COUNT, validJson } from '~/types/utils'
import { ClassicFormPayload, hidesJsonMode } from './CollapsiblePrebidModule'
import { PrebidModuleClassicForm } from './PrebidModuleClassicForm'
import { PrebidModuleJsonForm } from './PrebidModuleJsonForm'
import { filterStatusOptions } from './PureAdmPrebidModulePage'

type PureAdmPrebidModuleEditFlyoutProps = {
  prebidModule: PrebidModule
  data: PrebidModuleDataBySite<PrebidModuleType>[]
  enabledGreenbidsAnalytics?: PrebidModuleDataBySite<'Analytics'>[]
  onMultipleEdit: (payload: MultipleEditPayload<PrebidModuleType>) => void
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${BaseInputElement}{
    width: 300px;
  }
`
const ModuleState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  & > div {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoRequiredSettings = styled.div`
  font-style: italic;
`

// FIXME 2024-08-29 NRA SLE: factorize with RadioButtonGroup`
const RadioInput = styled.input`
  opacity: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  position: absolute;
`

const AdvancedModeTitle = styled.div`
  ${Fonts.P2};
  ${Fonts.colors.King};
  font-weight: bold;
`

const AdvancedMode = styled(FoldingContent)`
  border: none;
`

const SearchBar = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`

const NoWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const SitesTable = styled(Table)`
  th {
    top: -8px;
    &:before {
      border-top: none;
    }
    &:last-child:before {
      border-right: none;
    }
    &:first-of-type:before {
      border-left: none;
    }
  }
  td {
    &:last-of-type {
      box-shadow: none;
    }
    &:first-of-type {
      box-shadow: none;
    }
  }
`

export const PureAdmPrebidModuleEditFlyout = ({ prebidModule, data, enabledGreenbidsAnalytics, onMultipleEdit }: PureAdmPrebidModuleEditFlyoutProps) => {
  const { code, formParams, defaultValues, type } = prebidModule
  const [selectedSites, setSelectedSites] = useState<Site['id'][]>([])
  const [selectedSiteToCopy, setSelectedSiteToCopy] = useState<string>('')
  const [isApplying, setIsApplying] = useState<boolean>(false)
  const [currentOption, setCurrentOption] = useState<string>('existing-setting')
  const toast = useToast()

  const {
    control: configControl,
    formState: { errors },
    setValue: setConfigValue,
    unregister: unregisterConfig,
    reset,
    trigger,
  } = useForm<ClassicFormPayload>({
    defaultValues: {
      config: {
        moduleData: defaultValues,
      },
    },
    mode: 'onChange',
  })

  const config = useWatch({ control: configControl, name: 'config' })

  const { control, reset: resetSite } = useForm<{ enabled: boolean; siteToCopy: string }>({
    defaultValues: {
      enabled: true,
      siteToCopy: '',
    },
  })

  const { enabled } = useWatch({ control })

  const jsonForm = useForm<{ jsonValue: string }>({
    defaultValues: { jsonValue: JSON.stringify(defaultValues, undefined, JSON_SPACE_COUNT) },
    mode: 'onChange',
  })

  const {
    control: jsonControl,
    formState: { errors: jsonErrors, isDirty: jsonDirty, isValid: jsonIsValid },
    setValue: setJsonValue,
  } = jsonForm

  const { control: filterControl } = useForm<{ search: string; status: string }>({
    defaultValues: {
      search: '',
      status: 'all',
    },
  })
  const { search, status } = useWatch({ control: filterControl })

  const jsonValue = useWatch({ control: jsonControl, name: 'jsonValue' })

  useEffect(() => {
    // a oneOf variant switch unregisters the branch being left, and react-hook-form prunes the parents it empties:
    // this form only holds `config.moduleData`, so `config` itself can be gone, which means no setting is set
    setJsonValue('jsonValue', JSON.stringify(config?.moduleData ?? {}, undefined, JSON_SPACE_COUNT))
  }, [config])

  useEffect(() => {
    if (validJson(jsonValue)) {
      setConfigValue('config.moduleData', JSON.parse(jsonValue), { shouldValidate: jsonDirty })
    }
  }, [jsonValue])

  const searchedData = handleTableSearch(data, search || '', ['label'])
  const displayedSites = searchedData.filter((a) => (status === 'active' ? a.enabled : status === 'inactive' ? !a.enabled : true))

  const selectSites: SelectOptionProp[] = displayedSites
    .filter(
      (a) =>
        (a.moduleData?.params && Object.values(a.moduleData?.params).every((param) => param)) ||
        (a.moduleData?.storage && Object.values(a.moduleData?.storage).every((storage) => storage)) ||
        (a.moduleData?.options && Object.values(a.moduleData?.options).every((option) => option))
    )
    .map((a) => ({
      value: a.siteId,
      label: a.label,
      trackingLabel: 'adm-integrations-modules-multiple-edition-site-set',
    }))

  const isSelected = (row: PrebidModuleDataBySite<PrebidModuleType>) => selectedSites.some((site) => site === row.siteId)

  const onSelection = (row: PrebidModuleDataBySite<PrebidModuleType>) => {
    if (isSelected(row)) {
      setSelectedSites(selectedSites.filter((s) => s !== row.siteId))
    } else {
      setSelectedSites([...selectedSites, row.siteId])
    }
  }

  function getUniqueListBy(arr: Site['id'][]) {
    return Array.from(new Set(arr))
  }

  const onSelectAll = () => {
    if (displayedSites.every(isSelected)) {
      setSelectedSites(selectedSites.filter((s) => !displayedSites.some((a) => a.siteId === s)))
    } else {
      setSelectedSites(getUniqueListBy([...selectedSites, ...displayedSites.map((s) => s.siteId)]))
    }
  }

  const onSiteOptionChange = (option: SelectOptionProp) => {
    setJsonValue('jsonValue', JSON.stringify(displayedSites.filter((s) => s.siteId === option.value)[0].moduleData))
    setSelectedSiteToCopy(option.value as string)
  }

  /** If Greenbids Analytics module is not set for a website, so the Greenbids RTD module cannot be activated on this site. */
  const isGreenbidsNotAllowed = (data: PrebidModuleDataBySite<PrebidModuleType>) => {
    return prebidModule.code === 'greenbidsRtdProvider' && !enabledGreenbidsAnalytics?.find((greenbidSiteConfig) => greenbidSiteConfig.siteId === data.siteId)
  }

  let columns: TableColumns = [
    {
      name: (
        <NoWrap>
          <Checkbox checked={displayedSites.every(isSelected)} label={'All sites'} onChange={onSelectAll} trackingLabel={'adm-integrations-modules-multiple-edition-select-all-sites-apply'} />
        </NoWrap>
      ),
      isSortable: false,
    },
    { name: <NoWrap>Current status</NoWrap>, isSortable: false },
  ]

  const sortDisplayedSites = handleTableSort(columns, displayedSites)

  const onSubmit = async () => {
    /*
     * Applying writes the settings over every selected site, so what is being applied has to be valid before the
     * first site is touched. The gate is `trigger()`'s own answer rather than `isValid`: the branch a oneOf switch
     * mounts registers after the last validation run, and the form has been seen holding an error while `isValid`
     * still read true — which applied an empty config over the sites that had one.
     */
    const isSettingsValid = enabled ? (await trigger()) && jsonIsValid : true
    if (currentOption === 'existing-setting' && !selectedSiteToCopy && enabled) {
      toast.alert('Please select at least one site to copy.')
    } else if (selectedSites.length === 0) {
      toast.alert('Please select at least one site.')
    } else if (!isSettingsValid) {
      toast.alert('Some settings have errors.')
    } else {
      setIsApplying(true)
      onMultipleEdit({ enabled: !!enabled, selectedSites, moduleData: { name: code, ...config?.moduleData } })
    }
  }

  return (
    <Flyout.Content>
      <Flyout.Title>Multiple edit</Flyout.Title>
      <Flyout.Actions>
        <Button
          variant={'primary'}
          onClick={onSubmit}
          disabled={isApplying}
          iconSpin={isApplying}
          iconName={isApplying ? 'pubstack' : undefined}
          trackingLabel={'adm-integrations-modules-multiple-edition-save'}
        >
          Apply
        </Button>
      </Flyout.Actions>
      <Flyout.Body>
        <Wrapper>
          <p>Define module state and settings to apply to sites selection.</p>
          <ModuleState>
            <p>Module state</p>
            <Controller
              control={control}
              name={'enabled'}
              render={({ field: { onChange, value } }) => (
                <div>
                  <Toggle
                    value={value}
                    onClick={async () => {
                      onChange(!value)
                    }}
                    trackingLabel={value ? 'adm-integrations-modules-multiple-edition-module-state-unset' : 'adm-integrations-modules-multiple-edition-module-state-set'}
                  />
                  {value ? 'Enable module on following selection' : 'Disable module on following selection'}
                </div>
              )}
            />
          </ModuleState>
          {enabled && (
            <Section>
              <p>Settings</p>
              <>
                <RadioItemWrapper selected={currentOption === 'existing-setting'} disabled={false}>
                  <RadioInput
                    type={'radio'}
                    onClick={() => {
                      /* A native radio reports a click on the option already on, and the reset below is meant for the
                         switch, not for a second click on it: going through with it would drop the site being copied. */
                      if (currentOption === 'existing-setting') {
                        return
                      }
                      setCurrentOption('existing-setting')
                      resetSite()
                      setSelectedSiteToCopy('')
                    }}
                    data-tracking={'adm-integrations-modules-multiple-edition-duplicated-settings-set'}
                    name={'existing'}
                  />
                  <BulletIcon name={'radio'} width={'20px'} />
                  <span>Duplicate existing setting from site</span>
                </RadioItemWrapper>
                {currentOption === 'existing-setting' && (
                  <FormWrapper>
                    <Select options={selectSites} control={control} label={'Site setting to copy'} name={'siteToCopy'} onChange={onSiteOptionChange} searchable={true} />
                    {selectedSiteToCopy !== '' && (
                      <FormProvider {...jsonForm}>
                        <JSONEditorInput name={'jsonValue'} disabled={true} control={jsonControl} />
                      </FormProvider>
                    )}
                  </FormWrapper>
                )}

                <RadioItemWrapper selected={currentOption === 'new-setting'} disabled={false}>
                  <RadioInput
                    type={'radio'}
                    onClick={() => {
                      /* Same here: the `reset()` empties the settings the other option was holding, and a second click
                         on this one would empty the form being filled — behind inputs that keep showing their value. */
                      if (currentOption === 'new-setting') {
                        return
                      }
                      setCurrentOption('new-setting')
                      reset()
                    }}
                    data-tracking={'adm-integrations-modules-multiple-edition-new-settings-set'}
                    name={'new'}
                  />
                  <BulletIcon name={'radio'} width={'20px'} />
                  <span>New settings</span>
                </RadioItemWrapper>
                {currentOption === 'new-setting' && (
                  <>
                    {Object.keys(formParams).length || type === 'RTD' ? (
                      <PrebidModuleClassicForm prebidModule={prebidModule} control={configControl} errors={errors} setValue={setConfigValue} unregister={unregisterConfig} />
                    ) : (
                      <NoRequiredSettings>
                        <div>No required settings.</div>
                        <div>You can set optional settings in advanced mode.</div>
                      </NoRequiredSettings>
                    )}
                    {!hidesJsonMode(formParams) && (
                      <AdvancedMode title={<AdvancedModeTitle>Advanced mode (JSON)</AdvancedModeTitle>} isFolding={true} foldingIconColor={Colors.King}>
                        <FormProvider {...jsonForm}>
                          <PrebidModuleJsonForm control={jsonControl} errors={jsonErrors} />
                        </FormProvider>
                      </AdvancedMode>
                    )}
                  </>
                )}
              </>
            </Section>
          )}
          <Section>
            <p>Apply to</p>
            <SearchBar>
              <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={filterControl} />
              <Select name={'status'} options={filterStatusOptions} label={'Status'} control={filterControl} />
            </SearchBar>
            <SitesTable columns={columns} isLoading={false}>
              {sortDisplayedSites.map((row) => (
                <TableRow
                  key={row.siteId}
                  archived={isGreenbidsNotAllowed(row)}
                  clickable={{
                    /* The whole row toggles the site, the box included: it is a plain `tr`, so the click on the box
                       reaches this handler on its way up and is the one act of selecting. Nothing is prevented here —
                       cancelling the click would leave the box's own checked state behind the selection it shows. */
                    onClick: () => {
                      if (!isGreenbidsNotAllowed(row)) {
                        onSelection(row)
                      }
                    },
                    trackingLabel: isSelected(row) ? 'adm-integrations-modules-multiple-select-row-unset' : 'adm-integrations-modules-multiple-select-row-set',
                  }}
                >
                  <TableCell>
                    <NoWrap>
                      {/* The name is the cell's own text, below, so the box carries it for screen readers only */}
                      <Checkbox
                        aria-label={row.label}
                        checked={isSelected(row)}
                        disabled={isGreenbidsNotAllowed(row)}
                        trackingLabel={isSelected(row) ? 'adm-integrations-modules-multiple-select-row-unset' : 'adm-integrations-modules-multiple-select-row-set'}
                      />
                      {isGreenbidsNotAllowed(row) ? <Tooltip title={'Please enable greenbids analytics module first'}>{row.label}</Tooltip> : <span>{row.label}</span>}
                    </NoWrap>
                  </TableCell>
                  <TableCell>
                    <Status state={row.enabled ? 'active' : 'inactive'}>{row.enabled ? 'Active' : 'Not used'}</Status>
                  </TableCell>
                </TableRow>
              ))}
            </SitesTable>
          </Section>
        </Wrapper>
      </Flyout.Body>
    </Flyout.Content>
  )
}
